import {getWithAuth, postWithAuth} from '@/api'
import {TCountable} from '@/types'
import {
    ECOMClients,
    ECOMSummaryResponse,
    EpaySummary,
    EpaySummaryRequest,
    POSSummaryResponse,
    SettlementsSummaryData,
    SettlementsSummaryResponse
} from '@/stores/reports/models'
import {DoughnutChartType} from '@/pages/Reports/components'
import {POSTopSummaryRequest} from '@/stores'
import {SettlementsSummary} from '@/stores/reports/models/SettlementsSummary'

export type ECOMTransactionSummaryRequest = {
    from: string
    to: string
    currency?: string
    interval?: string
    status?: string
}

export type ECOMTransactionSummaryItem = {
    amount: number
    currency: string
    date: string
    status?: string
} & TCountable

export type PosTransactionSummaryItem = {
    amount: number
    currency: string
    value: string
} & TCountable

export type SettlementsSummaryItem = {
    from: string
    to: string
    currency: string
}

export type ECOMTransactionSummaryResponse = ECOMTransactionSummaryItem[]

export type POSTransactionSummaryResponse = {
    data: ECOMTransactionSummaryItem[]
}

export type POSSummary = ECOMSummaryResponse

export type POSDoughnutTransactionSummaryResponse = {
    all: POSSummary
    successful: POSSummary
    failed: POSSummary
}

export type SettlementsSummaryDataResponse = {
    data: SettlementsSummaryData[]
    totalCount: number
}

export const getECOMTransactionsSummary = (request: ECOMTransactionSummaryRequest) =>
    getWithAuth<ECOMTransactionSummaryResponse>('/oppapi/v1/ecom/transactions/summary', request)

export const getSettlementsTransactionsSummary = (
    request: SettlementsSummaryItem,
    signal?: AbortSignal
) =>
    postWithAuth<SettlementsSummaryResponse[]>(
        '/oppapi/v1/settlements/overview',
        request,
        undefined,
        undefined,
        signal
    )

export const getPOSTransactionsSummary = (request: ECOMTransactionSummaryRequest) =>
    postWithAuth<POSSummaryResponse>('/oppapi/v1/pos/transactions/summary', request)

export const getPOSTransactionsSummaryV2 = (
    request: ECOMTransactionSummaryRequest,
    signal?: AbortSignal
) =>
    postWithAuth<POSTransactionSummaryResponse>(
        '/oppapi/v2/pos/transactions/summary',
        request,
        undefined,
        undefined,
        signal
    )

export const getSettlementsSummaryData = (
    request: SettlementsSummaryItem & {page: number; size: number},
    signal?: AbortSignal
) =>
    postWithAuth<SettlementsSummaryDataResponse>(
        '/oppapi/v1/settlements/summary',
        request,
        undefined,
        undefined,
        signal
    )

export const getPOSTopSummaryByType = (request: POSTopSummaryRequest, type: DoughnutChartType) => {
    switch (type) {
        case DoughnutChartType.CardScheme:
            return postWithAuth<POSDoughnutTransactionSummaryResponse>(
                `/oppapi/v1/pos/transactions/top-summary/${type}`,
                request
            )
        case DoughnutChartType.Issuer:
            return postWithAuth<POSDoughnutTransactionSummaryResponse>(
                `/oppapi/v1/pos/transactions/top-summary/${type}`,
                request
            )
        case DoughnutChartType.PaymentMethod:
            return getWithAuth<POSDoughnutTransactionSummaryResponse>(
                `/oppapi/v1/pos/transactions/top-summary/${type}`,
                request
            )
    }
}

export const getEpaySummary = (request: EpaySummaryRequest) =>
    getWithAuth<EpaySummary>('/oppapi/v1/ecom/transactions/status-summary', request)

export const getEpaySummaryPaymentMethods = (request, type) =>
    getWithAuth<ECOMSummaryResponse>(`/oppapi/v1/ecom/transactions/top-summary/${type}`, request)

export const getEpayClients = (request) =>
    getWithAuth<ECOMClients>('/oppapi/v1/ecom/transactions/customer-summary', request)

export const getSettlementsSummary = (request, signal?: AbortSignal) =>
    postWithAuth<SettlementsSummary[]>(
        '/oppapi/v1/settlements/total-summary',
        request,
        undefined,
        undefined,
        signal
    )
